/**
 * ActionSuggestionCard — Approval-required action card.
 *
 * Rendered when the agent suggests an action that needs user confirmation
 * (e.g. kicking off technology creation + market research).
 *
 * On approve, the card POSTs to the specified Django endpoint and shows
 * the result inline. The action only runs once; the card transitions to
 * a "queued" or "error" state after the user clicks.
 */
import React, { useState } from 'react';
import { Rocket, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { tokenManager } from '../lib/api';
import type { ActionSuggestion } from '../hooks/useAgentChat';

const API_URL = import.meta.env.VITE_API_URL || '';

type CardStatus = 'pending' | 'loading' | 'success' | 'error';

interface ActionSuggestionCardProps {
  suggestion: ActionSuggestion;
  onDismiss: () => void;
  /** Compact layout for the sidebar drawer */
  compact?: boolean;
}

const ActionSuggestionCard: React.FC<ActionSuggestionCardProps> = ({
  suggestion,
  onDismiss,
  compact = false,
}) => {
  const [status, setStatus] = useState<CardStatus>('pending');
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = async () => {
    setStatus('loading');
    setError(null);

    try {
      const token = tokenManager.getAccessToken();
      const response = await fetch(`${API_URL}${suggestion.endpoint}`, {
        method: suggestion.method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(suggestion.params),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || body.detail || `Request failed (${response.status})`);
      }

      const data = await response.json();
      setResult(data);
      setStatus('success');

      // Notify discovery progress component to start polling immediately
      window.dispatchEvent(new Event('discoveryJobStarted'));
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setStatus('error');
    }
  };

  // --- Pending state: show approve / dismiss ---
  if (status === 'pending') {
    return (
      <div className={`flex flex-col gap-2 ${compact ? 'px-3 py-2' : 'px-4 py-3'} bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 min-w-0">
            <Rocket className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />
            <div className="min-w-0">
              <p className={`font-medium text-amber-800 dark:text-amber-200 ${compact ? 'text-xs' : 'text-sm'}`}>
                {suggestion.label}
              </p>
              <p className={`text-amber-700 dark:text-amber-300 mt-0.5 ${compact ? 'text-xs' : 'text-sm'} opacity-80`}>
                {suggestion.description}
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="p-1 hover:bg-amber-200 dark:hover:bg-amber-800 rounded shrink-0"
            title="Dismiss"
          >
            <X className="w-3 h-3 text-amber-600 dark:text-amber-400" />
          </button>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <button
            onClick={handleApprove}
            className={`flex items-center gap-1.5 ${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-md transition-colors`}
          >
            <Rocket className="w-3.5 h-3.5" />
            Approve &amp; Start
          </button>
          <button
            onClick={onDismiss}
            className={`${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800/40 rounded-md transition-colors`}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // --- Loading state ---
  if (status === 'loading') {
    return (
      <div className={`flex items-center gap-2 ${compact ? 'px-3 py-2' : 'px-4 py-3'} bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg`}>
        <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />
        <span className={`text-blue-700 dark:text-blue-300 ${compact ? 'text-xs' : 'text-sm'}`}>
          Queuing task...
        </span>
      </div>
    );
  }

  // --- Success state ---
  if (status === 'success') {
    return (
      <div className={`flex items-start gap-2 ${compact ? 'px-3 py-2' : 'px-4 py-3'} bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg`}>
        <CheckCircle className="w-4 h-4 mt-0.5 text-green-600 dark:text-green-400 shrink-0" />
        <div className="min-w-0">
          <p className={`font-medium text-green-800 dark:text-green-200 ${compact ? 'text-xs' : 'text-sm'}`}>
            {result?.message || 'Task queued successfully'}
          </p>
          {result?.task_id && (
            <p className={`text-green-700 dark:text-green-300 mt-0.5 ${compact ? 'text-[10px]' : 'text-xs'} opacity-70 font-mono`}>
              Task ID: {result.task_id}
            </p>
          )}
        </div>
      </div>
    );
  }

  // --- Error state ---
  return (
    <div className={`flex flex-col gap-2 ${compact ? 'px-3 py-2' : 'px-4 py-3'} bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg`}>
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 text-red-600 dark:text-red-400 shrink-0" />
        <div className="min-w-0">
          <p className={`font-medium text-red-800 dark:text-red-200 ${compact ? 'text-xs' : 'text-sm'}`}>
            Failed to start task
          </p>
          <p className={`text-red-700 dark:text-red-300 mt-0.5 ${compact ? 'text-xs' : 'text-sm'} opacity-80`}>
            {error}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <button
          onClick={() => { setStatus('pending'); setError(null); }}
          className={`${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-800/40 border border-red-300 dark:border-red-700 rounded-md transition-colors`}
        >
          Retry
        </button>
        <button
          onClick={onDismiss}
          className={`${compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800/40 rounded-md transition-colors`}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default ActionSuggestionCard;
